import { useState } from 'react';
import { getReviewsByProduct, getReviewStats } from '@/mocks/reviews';

interface ReviewsSectionProps {
  productId: string;
}

export default function ReviewsSection({ productId }: ReviewsSectionProps) {
  const [helpfulClicked, setHelpfulClicked] = useState<Set<string>>(new Set());
  const reviews = getReviewsByProduct(productId);
  const stats = getReviewStats(productId);

  const toggleHelpful = (id: string) => {
    setHelpfulClicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (reviews.length === 0) {
    return (
      <div className="py-8 text-center">
        <i className="ri-chat-1-line text-3xl text-foreground-400 mb-2" />
        <p className="text-sm text-foreground-500">No reviews yet. Be the first to review this product!</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="flex flex-col md:flex-row gap-6 md:gap-10">
        <div className="flex flex-col items-center md:items-start">
          <div className="flex items-baseline gap-2">
            <span className="text-5xl font-bold text-foreground-950 font-heading">{stats.average}</span>
            <span className="text-sm text-foreground-500">out of 5</span>
          </div>
          <div className="flex items-center gap-0.5 mt-1">
            {[1, 2, 3, 4, 5].map((s) => (
              <i
                key={s}
                className={`ri-star-fill text-base ${s <= Math.round(stats.average) ? 'text-accent-500' : 'text-background-300'}`}
              />
            ))}
          </div>
          <p className="text-sm text-foreground-500 mt-1">{stats.total} ratings</p>
        </div>

        <div className="flex-1 max-w-md space-y-1.5">
          {[5, 4, 3, 2, 1].map((star) => {
            const count = stats.counts[star as 1 | 2 | 3 | 4 | 5];
            const pct = stats.total > 0 ? (count / stats.total) * 100 : 0;
            return (
              <div key={star} className="flex items-center gap-3">
                <span className="text-sm text-foreground-700 w-3">{star}</span>
                <i className="ri-star-fill text-xs text-accent-500" />
                <div className="flex-1 h-2 rounded-full bg-background-200 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-accent-500 transition-all"
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <span className="text-xs text-foreground-500 w-8 text-right">{count}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Review List */}
      <div className="space-y-4">
        {reviews.map((review) => (
          <div
            key={review.id}
            className="border border-background-200/60 rounded-xl p-4 md:p-5 bg-background-50"
          >
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                  <span className="text-xs font-bold text-primary-700">
                    {review.userName.split(' ').map((n) => n[0]).join('')}
                  </span>
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground-900">{review.userName}</p>
                  <div className="flex items-center gap-2">
                    {review.verified && (
                      <span className="text-xs text-green-700 flex items-center gap-0.5">
                        <i className="ri-checkbox-circle-fill" />
                        Verified Purchase
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <span className="text-xs text-foreground-500">{review.date}</span>
            </div>

            <div className="flex items-center gap-1 mb-1">
              {[1, 2, 3, 4, 5].map((s) => (
                <i
                  key={s}
                  className={`ri-star-fill text-xs ${s <= review.rating ? 'text-accent-500' : 'text-background-300'}`}
                />
              ))}
            </div>

            <h4 className="text-sm font-semibold text-foreground-900 mb-1">{review.title}</h4>
            <p className="text-sm text-foreground-700 leading-relaxed">{review.comment}</p>

            <div className="flex items-center gap-3 mt-3">
              <button
                type="button"
                onClick={() => toggleHelpful(review.id)}
                className={`flex items-center gap-1.5 text-xs font-medium transition-colors ${
                  helpfulClicked.has(review.id)
                    ? 'text-primary-600'
                    : 'text-foreground-500 hover:text-foreground-800'
                }`}
              >
                <i className={helpfulClicked.has(review.id) ? 'ri-thumb-up-fill' : 'ri-thumb-up-line'} />
                Helpful ({review.helpfulCount + (helpfulClicked.has(review.id) ? 1 : 0)})
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}